// ============================================================================
// SettingsView — bounded personal/preferences page (NOT an admin console).
//
// Answers: "Who am I in ReferralGate, what organization am I inside, what access
// do I have, and how do I prefer the app to behave?"
//
// Sections: Account (name edit, email read-only, change password) · Access
// (role / tenant / specialties, read-only) · Preferences (theme, referral-ping
// notifications) · Security (sign out).
//
// Writes ONLY safe self fields to users/{uid} via updateUserSettings:
//   name, preferences.theme, preferences.notifications.referralPings,
//   settingsUpdatedAt. Never role, tenantId, specialtyIds, status, or PHI.
// Password is an action (Firebase Auth reauth + updatePassword), never stored.
// ============================================================================

import React, { useMemo, useState } from 'react';
import {
  User, Mail, KeyRound, Building2, ShieldCheck, Stethoscope, Bell, Sun, Moon,
  LogOut, Loader2, Check, AlertTriangle, X, BookOpen, Compass,
} from 'lucide-react';
import { useAuth } from '../auth/AuthContext.jsx';
import { updateUserSettings } from '../core/store.firebase.js';
import { ROLE_DISPLAY, ROLES } from '../core/rbac.js';
import { getCurrentUser } from '../core/auth.firebase.js';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from 'firebase/auth';
import RoleOrientationCard from '../learning/RoleOrientationCard.jsx';
import { getOrientationCard } from '../learning/roleLearningMaps.js';

const PASSWORD_MIN = 8;

// Apply the chosen theme to the document root. Both Dark and Light are
// token-driven via html[data-theme] (see src/styles/tokens.css).
function applyTheme(theme) {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', theme === 'light' ? 'light' : 'dark');
  try { localStorage.setItem('rg_theme', theme); } catch { /* ignore */ }
}

function plainPasswordError(err) {
  const code = err?.code || '';
  if (code.includes('wrong-password') || code.includes('invalid-credential')) {
    return 'Current password is incorrect.';
  }
  if (code.includes('weak-password')) return `New password is too weak (minimum ${PASSWORD_MIN} characters).`;
  if (code.includes('requires-recent-login')) return 'For security, sign out and sign in again, then retry.';
  if (code.includes('too-many-requests')) return 'Too many attempts. Please wait a moment and try again.';
  if (code.includes('network')) return 'Network error. Check your connection and try again.';
  return err?.message ? String(err.message).replace('Firebase: ', '') : 'Could not change password.';
}

// ── Small presentational helpers ─────────────────────────────────────────────

function SectionCard({ icon: Icon, title, children }) {
  return (
    <section className="rounded-2xl border border-slate-700/40 bg-slate-900/40 p-5 space-y-4">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-violet-400" />}
        <h2 className="text-sm font-semibold text-white">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function ReadOnlyRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-slate-800/50 last:border-0">
      <span className="text-xs text-slate-500 uppercase tracking-wide">{label}</span>
      <span className="text-sm text-slate-200 text-right break-words">{value}</span>
    </div>
  );
}

function Toggle({ options, value, onChange, disabled }) {
  return (
    <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-slate-800/60">
      {options.map((opt) => (
        <button
          key={opt.id}
          onClick={() => !disabled && opt.id !== value && onChange(opt.id)}
          disabled={disabled}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all disabled:opacity-50 ${
            value === opt.id ? 'bg-violet-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-700/60'
          }`}
        >
          {opt.icon && <opt.icon className="w-3.5 h-3.5" />}
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ── Change password modal ────────────────────────────────────────────────────

function ChangePasswordModal({ onClose }) {
  const fbUser = getCurrentUser();
  const hasPasswordProvider = !!fbUser?.providerData?.some((p) => p.providerId === 'password');

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const submit = async () => {
    setError('');
    if (!current) { setError('Enter your current password.'); return; }
    if (!next) { setError('Enter a new password.'); return; }
    if (next.length < PASSWORD_MIN) { setError(`New password must be at least ${PASSWORD_MIN} characters.`); return; }
    if (next !== confirm) { setError('New password and confirmation do not match.'); return; }
    if (!fbUser?.email) { setError('No signed-in account found. Sign in again and retry.'); return; }

    setBusy(true);
    try {
      const cred = EmailAuthProvider.credential(fbUser.email, current);
      await reauthenticateWithCredential(fbUser, cred);
      await updatePassword(fbUser, next);
      setDone(true);              // success — clear fields only now
      setCurrent(''); setNext(''); setConfirm('');
    } catch (err) {
      console.error('[Settings] password change failed:', err?.code || err?.message);
      setError(plainPasswordError(err)); // keep modal open, keep fields
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/75 flex items-center justify-center px-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-md rounded-2xl border border-slate-700/50 bg-slate-900 shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800/60">
          <h3 className="text-sm font-semibold text-white inline-flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-violet-400" /> Change password
          </h3>
          <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-4">
          {!hasPasswordProvider ? (
            <p className="text-sm text-slate-300">Password is managed by your sign-in provider.</p>
          ) : done ? (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/25">
              <Check className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-emerald-300">Password changed successfully.</p>
            </div>
          ) : (
            <>
              {[
                { label: 'Current password', val: current, set: setCurrent, auto: 'current-password' },
                { label: 'New password', val: next, set: setNext, auto: 'new-password' },
                { label: 'Confirm new password', val: confirm, set: setConfirm, auto: 'new-password' },
              ].map((f) => (
                <div key={f.label}>
                  <label className="block text-[10px] uppercase tracking-wide text-slate-500 mb-1">{f.label}</label>
                  <input
                    type="password"
                    autoComplete={f.auto}
                    value={f.val}
                    onChange={(e) => f.set(e.target.value)}
                    disabled={busy}
                    className="w-full px-3 py-2 rounded-lg bg-slate-800/60 border border-slate-700/50 text-sm text-white focus:outline-none focus:border-violet-500/50 disabled:opacity-50"
                  />
                </div>
              ))}
              <p className="text-[11px] text-slate-500">New password must be at least {PASSWORD_MIN} characters.</p>
              {error && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/25">
                  <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-300">{error}</p>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-800/60">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-700/50 text-slate-300 hover:text-white text-sm font-medium transition-all">
            {done || !hasPasswordProvider ? 'Close' : 'Cancel'}
          </button>
          {hasPasswordProvider && !done && (
            <button
              onClick={submit}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium disabled:opacity-50 transition-all"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Update password
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main view ────────────────────────────────────────────────────────────────

export default function SettingsView({ onNavigate, onLogout }) {
  const { user, tenant } = useAuth();

  const initialName = user?.displayName ?? user?.name ?? '';
  const [name, setName] = useState(initialName);
  const [savedName, setSavedName] = useState(initialName);
  const [nameBusy, setNameBusy] = useState(false);
  const [nameMsg, setNameMsg] = useState(null); // { type: 'ok'|'err', text }

  // Initialize the toggle from the LIVE applied theme (root data-theme attribute),
  // NOT from user.preferences. The in-memory profile is not refreshed after a save,
  // so reading it would show a stale selection after navigating away and back. The
  // data-theme attribute is always the current truth (maintained by the pre-paint
  // script, App's theme sync, and applyTheme; a failed save reverts it).
  const getLiveTheme = () => {
    if (typeof document !== 'undefined') {
      const attr = document.documentElement.getAttribute('data-theme');
      if (attr === 'light' || attr === 'dark') return attr;
    }
    return user?.preferences?.theme === 'dark' ? 'dark' : 'light';
  };
  const initialPings = user?.preferences?.notifications?.referralPings !== false; // default on
  const initialCollapsed = user?.preferences?.learning?.defaultCollapsed === true; // default expanded
  const [theme, setTheme] = useState(getLiveTheme);
  const [pings, setPings] = useState(initialPings);
  const [learningCollapsed, setLearningCollapsed] = useState(initialCollapsed);
  const [prefBusy, setPrefBusy] = useState(false);
  const [prefMsg, setPrefMsg] = useState(null);

  const [showPwModal, setShowPwModal] = useState(false);
  const [showOrientation, setShowOrientation] = useState(false);

  const uid = user?.uid || user?.id;
  const roleName = ROLE_DISPLAY[user?.role]?.name ?? user?.role ?? '—';
  const isPlatform = user?.role === ROLES.PLATFORM_ADMIN;
  // Role Orientation (manual). Content is role-specific; the row is disabled when a
  // role has no orientation card. Opening here writes NOTHING (the first-login
  // auto-open marker is owned by App, not by manual Settings opens).
  const role = user?.role;
  const orientationCard = getOrientationCard(role);
  const orientationColor = ROLE_DISPLAY[role]?.color || 'slate';

  const tenantLabel = useMemo(() => {
    if (tenant?.name) return tenant.name;
    if (isPlatform && !user?.tenantId) return 'Platform scope';
    return user?.tenantId || '—';
  }, [tenant, isPlatform, user?.tenantId]);

  const specialties = Array.isArray(user?.specialtyIds) ? user.specialtyIds : [];
  const nameChanged = name.trim() !== savedName.trim() && name.trim().length > 0;

  // Persist the COMPLETE preferences object (updateDoc replaces the map, so we
  // always send theme + notifications + learning together to preserve all three).
  const persistPreferences = async (nextTheme, nextPings, nextCollapsed) => {
    return updateUserSettings(uid, {
      preferences: {
        theme: nextTheme,
        notifications: { referralPings: nextPings },
        learning: { defaultCollapsed: nextCollapsed },
      },
    });
  };

  const saveName = async () => {
    if (!nameChanged || nameBusy) return;
    setNameBusy(true); setNameMsg(null);
    try {
      await updateUserSettings(uid, { name: name.trim() });
      setSavedName(name.trim());
      setNameMsg({ type: 'ok', text: 'Name saved.' });
    } catch (err) {
      console.error('[Settings] save name failed:', err?.message || err);
      setNameMsg({ type: 'err', text: 'Could not save your name. Please try again.' });
    } finally {
      setNameBusy(false);
    }
  };

  const changeTheme = async (nextTheme) => {
    if (prefBusy || nextTheme === theme) return;
    const prev = theme;
    setPrefBusy(true); setPrefMsg(null);
    setTheme(nextTheme); applyTheme(nextTheme);        // optimistic
    try {
      await persistPreferences(nextTheme, pings, learningCollapsed);
      setPrefMsg({ type: 'ok', text: 'Preferences saved.' });
    } catch (err) {
      console.error('[Settings] theme save failed:', err?.message || err);
      setTheme(prev); applyTheme(prev);                // revert on failure
      setPrefMsg({ type: 'err', text: 'Could not save theme. Reverted.' });
    } finally {
      setPrefBusy(false);
    }
  };

  const changePings = async (nextOn) => {
    if (prefBusy || nextOn === pings) return;
    const prev = pings;
    setPrefBusy(true); setPrefMsg(null);
    setPings(nextOn);                                  // optimistic
    try {
      await persistPreferences(theme, nextOn, learningCollapsed);
      setPrefMsg({ type: 'ok', text: 'Preferences saved.' });
    } catch (err) {
      console.error('[Settings] notifications save failed:', err?.message || err);
      setPings(prev);                                  // revert on failure
      setPrefMsg({ type: 'err', text: 'Could not save notification preference. Reverted.' });
    } finally {
      setPrefBusy(false);
    }
  };

  const changeLearning = async (nextCollapsed) => {
    if (prefBusy || nextCollapsed === learningCollapsed) return;
    const prev = learningCollapsed;
    setPrefBusy(true); setPrefMsg(null);
    setLearningCollapsed(nextCollapsed);               // optimistic
    try {
      await persistPreferences(theme, pings, nextCollapsed);
      setPrefMsg({ type: 'ok', text: 'Preferences saved.' });
    } catch (err) {
      console.error('[Settings] learning preference save failed:', err?.message || err);
      setLearningCollapsed(prev);                      // revert on failure
      setPrefMsg({ type: 'err', text: 'Could not save learning preference. Reverted.' });
    } finally {
      setPrefBusy(false);
    }
  };

  // Fail-fast: Settings is only reachable when signed in, but guard anyway.
  if (!user || !uid) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <div className="flex items-start gap-2 p-4 rounded-xl bg-red-500/10 border border-red-500/25">
          <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm text-red-300">Your profile could not be loaded.</p>
            <button
              onClick={() => onNavigate?.('dashboard')}
              className="mt-2 px-3 py-1.5 rounded-lg border border-slate-700/50 text-slate-300 hover:text-white text-xs font-medium"
            >
              Go to dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
          <User className="w-5 h-5 text-violet-400" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-white">Settings</h1>
          <p className="text-xs text-slate-400">Manage your account, access context, and personal preferences.</p>
        </div>
      </div>

      {/* Account */}
      <SectionCard icon={User} title="Account">
        <div>
          <label className="block text-[10px] uppercase tracking-wide text-slate-500 mb-1">Name</label>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={name}
              onChange={(e) => { setName(e.target.value); setNameMsg(null); }}
              disabled={nameBusy}
              className="flex-1 px-3 py-2 rounded-lg bg-slate-800/60 border border-slate-700/50 text-sm text-white focus:outline-none focus:border-violet-500/50 disabled:opacity-50"
            />
            <button
              onClick={saveName}
              disabled={!nameChanged || nameBusy}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              {nameBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save
            </button>
          </div>
          {nameMsg && (
            <p className={`mt-1.5 text-xs ${nameMsg.type === 'ok' ? 'text-emerald-400' : 'text-red-400'}`}>{nameMsg.text}</p>
          )}
        </div>

        <div>
          <label className="block text-[10px] uppercase tracking-wide text-slate-500 mb-1">Email</label>
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800/40 border border-slate-700/40">
            <Mail className="w-4 h-4 text-slate-500 flex-shrink-0" />
            <span className="text-sm text-slate-300 truncate">{user.email || '—'}</span>
          </div>
          <p className="mt-1 text-[11px] text-slate-500">Email is managed by your sign-in account.</p>
        </div>

        <div>
          <label className="block text-[10px] uppercase tracking-wide text-slate-500 mb-1">Password</label>
          <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-slate-800/40 border border-slate-700/40">
            <span className="inline-flex items-center gap-2 text-sm text-slate-300">
              <KeyRound className="w-4 h-4 text-slate-500" /> Password
            </span>
            <button
              onClick={() => setShowPwModal(true)}
              className="px-3 py-1.5 rounded-lg border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-600 text-xs font-medium transition-all"
            >
              Change password
            </button>
          </div>
        </div>
      </SectionCard>

      {/* Access */}
      <SectionCard icon={ShieldCheck} title="Access">
        <div className="rounded-xl bg-slate-800/30 border border-slate-700/30 px-4 py-1">
          <ReadOnlyRow label="User type" value={isPlatform ? 'Platform Admin' : roleName} />
          <ReadOnlyRow label="Tenant" value={tenantLabel} />
          <ReadOnlyRow
            label="Assigned specialties"
            value={specialties.length ? (
              <span className="inline-flex flex-wrap gap-1 justify-end">
                {specialties.map((s) => (
                  <span key={s} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-700/40 border border-slate-600/30 text-xs text-slate-200">
                    <Stethoscope className="w-3 h-3 text-slate-400" />{s}
                  </span>
                ))}
              </span>
            ) : '—'}
          />
        </div>
        <p className="text-[11px] text-slate-500">
          Your role controls which ReferralGate areas you can access. Tenant and role changes are managed by an administrator.
        </p>
      </SectionCard>

      {/* Preferences */}
      <SectionCard icon={Building2} title="Preferences">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm text-slate-200">Theme</p>
            <p className="text-[11px] text-slate-500">Choose how the app looks for you.</p>
          </div>
          <Toggle
            value={theme}
            onChange={changeTheme}
            disabled={prefBusy}
            options={[
              { id: 'light', label: 'Light', icon: Sun },
              { id: 'dark', label: 'Dark', icon: Moon },
            ]}
          />
        </div>

        <div className="flex items-center justify-between gap-4 pt-2 border-t border-slate-800/50">
          <div>
            <p className="text-sm text-slate-200 inline-flex items-center gap-1.5"><Bell className="w-3.5 h-3.5 text-slate-400" /> Referral Pings notifications</p>
            <p className="text-[11px] text-slate-500">Show alerts for new Referral Pings addressed to you.</p>
          </div>
          <Toggle
            value={pings ? 'on' : 'off'}
            onChange={(v) => changePings(v === 'on')}
            disabled={prefBusy}
            options={[
              { id: 'on', label: 'On' },
              { id: 'off', label: 'Off' },
            ]}
          />
        </div>

        <div className="flex items-center justify-between gap-4 pt-2 border-t border-slate-800/50">
          <div>
            <p className="text-sm text-slate-200 inline-flex items-center gap-1.5"><BookOpen className="w-3.5 h-3.5 text-slate-400" /> Learning Guidance</p>
            <p className="text-[11px] text-slate-500">Choose how in-app learning cards appear while you work.</p>
          </div>
          <Toggle
            value={learningCollapsed ? 'collapsed' : 'expanded'}
            onChange={(v) => changeLearning(v === 'collapsed')}
            disabled={prefBusy}
            options={[
              { id: 'expanded', label: 'Expanded' },
              { id: 'collapsed', label: 'Collapsed' },
            ]}
          />
        </div>

        {/* Role Orientation — directly below Learning Guidance. Manual open only. */}
        <div className="flex items-center justify-between gap-4 pt-2 border-t border-slate-800/50">
          <div>
            <p className="text-sm text-slate-200 inline-flex items-center gap-1.5"><Compass className="w-3.5 h-3.5 text-slate-400" /> Role Orientation</p>
            <p className="text-[11px] text-slate-500">Review what your role does in ReferralGate and where your work starts.</p>
          </div>
          <button
            onClick={() => setShowOrientation(true)}
            disabled={!orientationCard}
            className="px-3 py-1.5 rounded-lg border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-600 text-xs font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            title={orientationCard ? 'Open Role Orientation' : 'Role orientation is not available for your role'}
          >
            Open Role Orientation
          </button>
        </div>

        {prefMsg && (
          <p className={`text-xs ${prefMsg.type === 'ok' ? 'text-emerald-400' : 'text-red-400'}`}>{prefMsg.text}</p>
        )}
      </SectionCard>

      {/* Security */}
      <SectionCard icon={LogOut} title="Security">
        <button
          onClick={() => onLogout?.()}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800/60 text-slate-300 hover:text-white hover:bg-slate-800 text-sm font-medium transition-all"
        >
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </SectionCard>

      {showPwModal && <ChangePasswordModal onClose={() => setShowPwModal(false)} />}

      {/* Role Orientation — manual open from Preferences. Writes NOTHING (no marker,
          no completion); the first-login auto-open marker is owned by App. */}
      {showOrientation && orientationCard && (
        <RoleOrientationCard
          orientationCard={orientationCard}
          roleColor={orientationColor}
          onStart={() => setShowOrientation(false)}
        />
      )}
    </div>
  );
}
